import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type {
  InvoicePaymentFilter,
  InvoiceStatusFilter,
  InvoiceViewMode,
} from "../invoice.constants";
import { InvoiceViewToggle } from "./InvoiceViewToggle";

interface InvoicesToolbarProps {
  search: string;
  statusFilter: InvoiceStatusFilter;
  paymentFilter: InvoicePaymentFilter;
  viewMode: InvoiceViewMode;
  filteredCount: number;
  totalCount: number;
  onSearchChange: (value: string) => void;
  onStatusFilterChange: (value: InvoiceStatusFilter) => void;
  onPaymentFilterChange: (value: InvoicePaymentFilter) => void;
  onViewModeChange: (mode: InvoiceViewMode) => void;
  onResetFilters: () => void;
}

export function InvoicesToolbar({
  search,
  statusFilter,
  paymentFilter,
  viewMode,
  filteredCount,
  totalCount,
  onSearchChange,
  onStatusFilterChange,
  onPaymentFilterChange,
  onViewModeChange,
  onResetFilters,
}: InvoicesToolbarProps) {
  const hasFilters = search || statusFilter !== "all" || paymentFilter !== "all";

  return (
    <div className="mb-4 grid gap-3 border-y border-border py-3 xl:grid-cols-[minmax(0,1fr)_auto] xl:items-center">
      <div className="grid min-w-0 gap-2 md:grid-cols-[minmax(240px,420px)_180px_180px_auto]">
        <div className="relative min-w-0">
          <Search
            size={14}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-subtle"
          />
          <Input
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Rechercher numéro, client, contact, ligne..."
            className="pl-9"
          />
        </div>

        <Select
          value={statusFilter}
          onValueChange={(value) => onStatusFilterChange(value as InvoiceStatusFilter)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="draft">Brouillons</SelectItem>
            <SelectItem value="issued">Émises</SelectItem>
            <SelectItem value="expired">Expirées</SelectItem>
            <SelectItem value="cancelled">Annulées</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={paymentFilter}
          onValueChange={(value) => onPaymentFilterChange(value as InvoicePaymentFilter)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Paiement" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les paiements</SelectItem>
            <SelectItem value="unpaid">Impayées</SelectItem>
            <SelectItem value="partial">Partielles</SelectItem>
            <SelectItem value="paid">Payées</SelectItem>
          </SelectContent>
        </Select>

        {hasFilters && (
          <Button type="button" variant="secondary" size="sm" onClick={onResetFilters}>
            Réinitialiser
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between xl:justify-end">
        <p className="text-[11.5px] text-muted-foreground">
          {filteredCount} résultat{filteredCount !== 1 ? "s" : ""}
          {filteredCount !== totalCount ? ` sur ${totalCount}` : ""}
        </p>
        <InvoiceViewToggle viewMode={viewMode} onViewModeChange={onViewModeChange} />
      </div>
    </div>
  );
}
